// ─── Public Status Page — the whole router ───────────────────────────
//
// react-router is NOT used here, and vite.config.ts splits it into its own
// `router-vendor` chunk specifically so it can't leak in: manualChunks emits one
// physical file per chunk name, so a shared `react-vendor` containing the router
// would force the ONE page whose job is to load when everything else is broken
// to download ~90 kB of routing it never calls. This page has two routes and no
// loaders, no nested layouts, no data revalidation and no view transitions —
// history.pushState plus a popstate listener is the entire requirement.
//
// Routes:
//   /                                 → overview, combined/default audience
//   /<audience-slug>                  → overview for that audience
//   /incident/<ref>                   → incident detail, combined audience
//   /<audience-slug>/incident/<ref>   → incident detail within an audience
//   /component/<id>                   → one system in full, combined audience
//   /<audience-slug>/component/<id>   → the same within an audience
//   /manage/<code>                    → a subscriber's own preferences
//   /manage?key=<manageKey>           → the same, as the confirm route redirects
//   /unsubscribe/<code>               → one-click unsubscribe, confirmed on screen
//   /status.html[…]                   → the `pnpm dev` / `vite preview` form of
//                                       all of the above (the entry is a real
//                                       file there; in production the status host
//                                       rewrites every path to it).

import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { isValidStatusSlug, parseIncidentRef } from '@pcp/shared';
import { parseDayParam } from './lib/day';

export type StatusRoute =
  | { name: 'overview'; audience: string | null }
  | { name: 'incident'; audience: string | null; ref: string }
  | { name: 'component'; audience: string | null; id: string }
  /**
   * One DAY of history, reached by clicking a cell in a 90-day bar.
   *
   * `componentId` null = the page-wide bar was clicked, so the page covers
   * everything; set = a system's own bar, and the page narrows to that system
   * AND ITS SUBTREE (the bar is rolled up, so its day page has to be too).
   * `date` is a plain YYYY-MM-DD in the READER's timezone — the same bucketing
   * the grid uses, so a cell and its page can never disagree.
   */
  | { name: 'day'; audience: string | null; date: string; componentId: string | null }
  /**
   * A subscriber's own page. NEVER audience-scoped: the link comes out of an
   * email, and the subscription is the subject — which audiences it covers is
   * something the page shows, not something the URL frames.
   *
   * `code` is the SHORT CODE from an email/SMS link; `key` is the full manage key,
   * which is what the confirm route redirects with. Exactly one is present.
   */
  | { name: 'manage'; code: string | null; key: string | null; error: boolean }
  | { name: 'unsubscribe'; code: string | null; key: string | null };

/** Short codes are Crockford base32, EXACTLY 10 characters — SHORT_CODE_LENGTH
 *  in services/status/subscribers.ts, and the same shape the auth guard's
 *  one-click rule matches. Validated so a junk segment degrades to "that link
 *  did not work" instead of a request we already know will fail. */
const SHORT_CODE_RE = /^[0-9A-HJKMNP-TV-Z]{10}$/i;
/**
 * A manage key is `randomBytes(32).toString('base64url')` — 43 characters of
 * base64url (see newSecret() in services/status/subscribers.ts).
 *
 * ⚠ THIS WAS WRITTEN AS A HEX REGEX, from an assumption rather than from the
 * generator, and it rejected every real key: the manage page fell straight to
 * "Linket virkede ikke" for a link that was perfectly valid (b-01557).
 *
 * Deliberately PERMISSIVE now, and not pinned to 43. The only job of this test
 * is to avoid firing a request for something that is obviously not a token — the
 * SERVER is the authority on whether a key resolves, and it already answers
 * identically for garbage, expired and never-existed. A stricter client-side
 * guess buys nothing and can only re-create the bug it was meant to prevent.
 */
const MANAGE_KEY_RE = /^[A-Za-z0-9_-]{20,200}$/;

/**
 * A component is addressed by its uuid: status_components carries no slug, and
 * its one unique text column (source_ref) is NULL for everything not imported
 * from StatusCast. Validated rather than trusted so a hand-typed segment
 * degrades to the overview instead of rendering a "not found" for a string that
 * was never an id in the first place.
 */
const COMPONENT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * When the entry is reached as a real file (`vite dev`, `vite preview`, or a
 * direct /status.html hit in production), every generated href must keep that
 * prefix or a reload would 404. Resolved ONCE at module load: a session cannot
 * migrate between the two forms.
 */
const BASE_PATH =
  typeof window !== 'undefined' && /^\/status\.html(\/|$)/.test(window.location.pathname) ? '/status.html' : '';

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment; // a malformed %-escape is still a literal segment
  }
}

/** Split a pathname into clean segments, minus the /status.html entry prefix. */
function segmentsOf(pathname: string): string[] {
  const segments = String(pathname || '/')
    .split('/')
    .filter(Boolean)
    .map(decodeSegment);
  if (segments[0] === 'status.html') segments.shift();
  return segments;
}

/**
 * Pure — exported so it can be reasoned about (and tested) without a window.
 *
 * `search` is needed for ONE reason: the API's confirm route and the SMS
 * short-link route both redirect with `?key=<manageKey>` (and `?error=` when the
 * link did not resolve), so the manage page has to be reachable that way as well
 * as by `/manage/<code>`. Optional, so every existing caller is unaffected.
 */
export function parseStatusPath(pathname: string, search = ''): StatusRoute {
  const segments = segmentsOf(pathname);
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const queryKey = (() => {
    const k = String(query.get('key') ?? '').trim();
    return MANAGE_KEY_RE.test(k) ? k : null;
  })();
  const queryError = Boolean(query.get('error'));

  if (segments.length === 0) return { name: 'overview', audience: null };

  // /incident/<ref>
  if (segments[0] === 'incident') {
    const ref = segments[1] ?? '';
    if (ref && parseIncidentRef(ref)) return { name: 'incident', audience: null, ref };
    return { name: 'overview', audience: null };
  }

  // /component/<id>. Must be tested BEFORE the audience check below, exactly
  // like the incident arm — 'component' is in STATUS_RESERVED_SLUGS so no
  // audience can ever be named it, and the two guards belong together.
  if (segments[0] === 'component') {
    const id = segments[1] ?? '';
    if (COMPONENT_ID_RE.test(id)) return { name: 'component', audience: null, id };
    return { name: 'overview', audience: null };
  }

  // /day/<YYYY-MM-DD>[/<component-id>]. Tested BEFORE the audience check, like
  // the incident and component arms — 'day' is in STATUS_RESERVED_SLUGS so no
  // audience can be named it.
  if (segments[0] === 'day') {
    return dayRoute(null, segments[1], segments[2]);
  }

  // /manage/<code> and /manage?key=<manageKey>. Never audience-scoped, and
  // tested before the audience check like the two arms above — 'manage' and
  // 'unsubscribe' are both in STATUS_RESERVED_SLUGS.
  if (segments[0] === 'manage') {
    const code = segments[1] ?? '';
    return {
      name: 'manage',
      code: SHORT_CODE_RE.test(code) ? code : null,
      key: queryKey,
      // An unresolvable link lands here with ?error= rather than on a 404, so
      // the page can say "that link did not work" without ever revealing
      // whether the code existed.
      error: queryError,
    };
  }

  // /unsubscribe/<code>. The HUMAN-facing form; a mail client's own unsubscribe
  // button POSTs straight to the API instead and never reaches this page.
  if (segments[0] === 'unsubscribe') {
    const code = segments[1] ?? '';
    return { name: 'unsubscribe', code: SHORT_CODE_RE.test(code) ? code : null, key: queryKey };
  }

  const audience = isValidStatusSlug(segments[0]) ? segments[0] : null;

  // /<audience>/incident/<ref>
  if (audience && segments[1] === 'incident') {
    const ref = segments[2] ?? '';
    if (ref && parseIncidentRef(ref)) return { name: 'incident', audience, ref };
    return { name: 'overview', audience };
  }

  // /<audience>/component/<id>
  if (audience && segments[1] === 'component') {
    const id = segments[2] ?? '';
    if (COMPONENT_ID_RE.test(id)) return { name: 'component', audience, id };
    return { name: 'overview', audience };
  }

  // /<audience>/day/<YYYY-MM-DD>[/<component-id>]
  if (audience && segments[1] === 'day') {
    return dayRoute(audience, segments[2], segments[3]);
  }

  // Anything else (an unknown or reserved first segment) is the default overview
  // rather than a 404 — a status page must always render something.
  return { name: 'overview', audience };
}

/** A /day route, or the overview when the date is not a real calendar day.
 *  Degrading rather than 404ing is this file's rule — a status page must always
 *  render something. */
function dayRoute(audience: string | null, date?: string, componentId?: string): StatusRoute {
  if (!date || !parseDayParam(date)) return { name: 'overview', audience };
  if (componentId && !COMPONENT_ID_RE.test(componentId)) return { name: 'overview', audience };
  return { name: 'day', audience, date, componentId: componentId || null };
}

/** The canonical path for a route — used for hrefs AND for pushState. */
export function buildStatusPath(route: StatusRoute): string {
  const parts: string[] = [];
  // The subscriber-facing routes carry no audience by design — see the union.
  if (route.name !== 'manage' && route.name !== 'unsubscribe' && route.audience) {
    parts.push(encodeURIComponent(route.audience));
  }
  if (route.name === 'incident') {
    parts.push('incident', encodeURIComponent(route.ref));
  } else if (route.name === 'component') {
    parts.push('component', encodeURIComponent(route.id));
  } else if (route.name === 'day') {
    parts.push('day', encodeURIComponent(route.date));
    if (route.componentId) parts.push(encodeURIComponent(route.componentId));
  } else if (route.name === 'manage' || route.name === 'unsubscribe') {
    // The short code is the canonical, shareable form. A manage KEY is a live
    // credential, so it stays in the query string the redirect put it in and is
    // never promoted into a path we would then push into history.
    parts.push(route.name);
    if (route.code) parts.push(encodeURIComponent(route.code));
    const path = `/${parts.join('/')}`;
    const qs = !route.code && route.key ? `?key=${encodeURIComponent(route.key)}` : '';
    return `${BASE_PATH}${path}${qs}`;
  }
  const path = parts.length ? `/${parts.join('/')}` : '/';
  return BASE_PATH ? `${BASE_PATH}${path === '/' ? '' : path}` : path;
}

export interface StatusRouter {
  route: StatusRoute;
  navigate: (route: StatusRoute) => void;
}

export function useStatusRouter(): StatusRouter {
  const [route, setRoute] = useState<StatusRoute>(() =>
    typeof window === 'undefined'
      ? parseStatusPath('/')
      : parseStatusPath(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const onPopState = () => setRoute(parseStatusPath(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((next: StatusRoute) => {
    const path = buildStatusPath(next);
    if (path !== `${window.location.pathname}${window.location.search}`) {
      window.history.pushState(null, '', path);
    }
    setRoute(next);
    // A route change here always swaps the whole main region, so the reader must
    // start at the top. 'auto', never 'smooth': index.css sets
    // `scroll-behavior: smooth` on <html>, and animating a full-page swap reads
    // as lag on a page people open because something is already wrong.
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return { route, navigate };
}

/**
 * TRUE only for a plain primary-button click with no modifier. Everything else
 * — middle click, Ctrl/Cmd click, Shift, Alt, or an already-prevented event —
 * must fall through to the browser so "open in a new tab", "download" and
 * "copy link address" keep working. That is why every navigable element on this
 * page is a real <a href> with a real URL and this helper on top, never a
 * <div onClick>.
 */
export function isPlainLeftClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  );
}

/** Ready-made onClick for an <a href={buildStatusPath(route)}>. */
export function linkHandler(
  navigate: (route: StatusRoute) => void,
  route: StatusRoute,
): (event: MouseEvent<HTMLElement>) => void {
  return (event) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(route);
  };
}
